import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import {
  useListNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation,
} from '../../store/notificationsApi';
import { useAuth } from '../../context/AuthContext';

// Bell icon → popover with recent notifications. Mounted in Navbar; only
// rendered when the user is authenticated (the API requires a session).
//
// Refresh strategy:
//   • RTK Query handles the initial fetch on mount.
//   • A custom 'trustlab:notification' window event (fired by the foreground
//     listener in usePushNotifications) triggers an immediate refetch when a
//     push lands while the page is open.
//   • Polls every 60s as a safety net for tabs that miss the event
//     (e.g. service worker re-installed).

const TIME_FMT = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
});

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return TIME_FMT.format(d);
};

const NotificationBell = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  // NO POLLING — see the note in admin/src/components/NotificationBell.js.
  // The 15-second "safety net" ran on every browser, including the ones
  // where push works perfectly, so it spent the API rate limit asking for
  // nothing. The bell updates when a push arrives and when it is opened.
  const { data, refetch, isFetching } = useListNotificationsQuery(20, {
    skip: !isAuthenticated,
  });
  const [markRead] = useMarkReadMutation();
  const [markAllRead, { isLoading: marking }] = useMarkAllReadMutation();
  const [deleteOne] = useDeleteNotificationMutation();
  const [deleteAll, { isLoading: clearing }] = useDeleteAllNotificationsMutation();

  // Refetch immediately when a push lands in the foreground.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onPush = () => refetch();
    window.addEventListener('trustlab:notification', onPush);
    return () => window.removeEventListener('trustlab:notification', onPush);
  }, [isAuthenticated, refetch]);

  if (!isAuthenticated) return null;

  const items = data?.notifications || [];
  const unread = data?.unread_count || 0;

  const onItemClick = async (n) => {
    setAnchor(null);
    if (!n.read_at) {
      try { await markRead(n._id).unwrap(); } catch { /* non-fatal */ }
    }
    if (n.url) navigate(n.url);
  };

  const onMarkAll = async (e) => {
    e.stopPropagation();
    try { await markAllRead().unwrap(); } catch { /* non-fatal */ }
  };

  const onDeleteOne = async (e, n) => {
    // Stop the parent row click from also firing (navigate + mark-read).
    e.stopPropagation();
    try { await deleteOne(n._id).unwrap(); } catch { /* non-fatal */ }
  };

  const onClearAll = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Clear all notifications? This cannot be undone.')) return;
    try { await deleteAll().unwrap(); } catch { /* non-fatal */ }
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Notifications"
        sx={{
          color: 'var(--charcoal)',
          border: '1px solid var(--border)',
          borderRadius: '50%',
          width: 36, height: 36, p: 0,
          '&:hover': { borderColor: 'var(--sage)', color: 'var(--sage)' },
        }}
      >
        <Badge
          badgeContent={unread}
          color="secondary"
          max={99}
          sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 800, top: 2, right: 2 } }}
        >
          {unread > 0
            ? <NotificationsActiveOutlinedIcon sx={{ fontSize: 18 }} />
            : <NotificationsNoneOutlinedIcon sx={{ fontSize: 18 }} />}
        </Badge>
      </IconButton>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1, width: { xs: 320, sm: 380 }, maxHeight: 480,
              borderRadius: '14px', border: '1px solid var(--border-l)',
              boxShadow: '0 12px 36px rgba(21,42,30,0.12)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
            Notifications
            {unread > 0 && (
              <Box component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 800, color: 'var(--sage)' }}>
                · {unread} new
              </Box>
            )}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {unread > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon sx={{ fontSize: 14 }} />}
                onClick={onMarkAll}
                disabled={marking}
                sx={{ textTransform: 'none', fontSize: 12, color: 'var(--sage)' }}
              >
                Mark all read
              </Button>
            )}
            {items.length > 0 && (
              <Tooltip title="Clear all">
                <IconButton
                  size="small"
                  onClick={onClearAll}
                  disabled={clearing}
                  sx={{ color: 'var(--stone)', '&:hover': { color: 'var(--red)' } }}
                >
                  <DeleteSweepOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Divider />

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {isFetching && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} sx={{ color: 'var(--sage)' }} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
              <Typography sx={{ fontSize: 32, mb: 1 }}>🔔</Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--stone)' }}>
                You're all caught up.
              </Typography>
            </Box>
          ) : (
            items.map((n) => {
              const isUnread = !n.read_at;
              return (
                <Box
                  key={n._id}
                  onClick={() => onItemClick(n)}
                  sx={{
                    px: 2, py: 1.5,
                    borderBottom: '1px solid var(--border-l)',
                    cursor: 'pointer',
                    bgcolor: isUnread ? 'var(--mint, #EEF8F2)' : '#fff',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: isUnread ? '#E0F2E8' : '#FAF6EF' },
                    '&:last-of-type': { borderBottom: 0 },
                    // Surface the row-delete button on hover (or always on touch).
                    '&:hover .notif-del, &:focus-within .notif-del': { opacity: 1 },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    {isUnread && (
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: 'var(--sage)', mt: '6px', flexShrink: 0,
                      }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, pl: isUnread ? 0 : 1.5 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: isUnread ? 700 : 600, color: 'var(--ink)', lineHeight: 1.3 }}>
                        {n.title}
                      </Typography>
                      {n.body && (
                        <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5, mt: 0.25 }}>
                          {n.body}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 11, color: 'var(--stone)', mt: 0.5 }}>
                        {formatTime(n.createdAt)}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete">
                      <IconButton
                        className="notif-del"
                        size="small"
                        onClick={(e) => onDeleteOne(e, n)}
                        sx={{
                          opacity: { xs: 1, sm: 0 },         // always visible on touch, hover-only on desktop
                          transition: 'opacity 0.15s',
                          color: 'var(--stone)',
                          flexShrink: 0, mt: -0.25,
                          '&:hover': { color: 'var(--red)' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
